/**
 * Access Pass landing-page FAQs.
 *
 * Answers are markdown rather than a bespoke runs structure so they go through
 * `PSRichText` — the same path event descriptions and T&Cs already take, and the
 * same shape the API hands us now that stored copy is markdown. The supported
 * set is exactly what that atom renders: paragraphs, bold/italic, and lists.
 *
 * These live here rather than inline in the landing page (where the showcase
 * and testimonial literals sit) because they're bulk authored copy, not a
 * handful of display fields — keeping them out keeps the screen readable, and
 * the swap to an endpoint stays a one-file change.
 *
 * Answers are flush-left in their template literals on purpose: four leading
 * spaces would make markdown read the line as a code block.
 */
export type Faq = {
  question: string;
  /** A stored markdown string — see PSRichText for what it renders. */
  answer: string;
};

export const FAQS: Faq[] = [
  {
    question: 'What is included with my Putting Scene Access Pass?',
    answer: `Your Putting Scene Access Pass unlocks access to some of Bangalore’s most exciting experiences — from invite-only events, exclusive previews to unique curated events, surprise goodies, and more! Think of it as your backstage pass to the city.`,
  },
  {
    question: 'How do I find out about these events?',
    answer: `You can stay updated through the Putting Scene app, website, and our Members Only WhatsApp group. Once you get your pass, we’ll add you to the group so you never miss what’s happening.`,
  },
  {
    question: 'Do I have to pay for these events?',
    answer: `It’s a mix of free and ticketed experiences. Most paid events are simple cover charges, and every event is designed to be worth showing up for.`,
  },
  {
    question: 'Can I bring my friends?',
    answer: `Some events are members-only, while others are +1 or open for groups. Each event will clearly mention the access details. If a hard cap is in place, no additional guests beyond the stated limit will be admitted, no exceptions.`,
  },
  {
    question:
      'Does having the Putting Scene Access Pass guarantee a ticket to every event?',
    answer: `Our venue partners like to keep experiences intimate and well-curated, so spots are limited and usually available on a first come, first served basis. If you see something you love, grab your spot quickly.`,
  },
  {
    question: 'How long is it valid for?',
    answer: `Your Access Pass is valid for one month and will auto-renew based on your selection during checkout. You can cancel anytime. And if you ever run into an issue, just reach out to us — we’ll always try to sort it out.`,
  },
  {
    question: 'What’s a no-show penalty?',
    // The one answer that needs more than a paragraph: a list, and real
    // paragraph breaks mid-answer. Both are free in markdown, which is why the
    // copy isn't carried in a bespoke runs structure.
    answer: `Life happens, we get it. If you can’t make it to an experience you’ve RSVP’d for, just let us know on **95387 18754** at least **24 hours before it starts**.

- **More than 24 hours’ notice:** No penalty at all.
- **Less than 24 hours’ notice (or a no-show):** You won’t be able to book Access Pass experiences for the next **10 days**.

Since spots are limited, late cancellations don’t give us enough time to offer them to other members waiting for a seat. A little heads-up goes a long way.

Running late? We will always build in a 10-minute grace window, but anyone arriving after that won’t be able to enter and will be marked as a last-minute no-show.`,
  },
  {
    question: 'Will I get a refund?',
    answer: `No`,
  },
  {
    question: 'Can I buy an Access Pass Trial Pass more than once?',
    answer: `Once you’ve got a taste, it’s time to upgrade. The Access Pass Trial Pass can only be purchased once per person.`,
  },
  {
    question: 'Are tickets transferrable?',
    answer: `Tickets are non-transferable, so ensure you plan well!`,
  },
  {
    question: 'What if I have food allergies?',
    answer: `Let us know via WhatsApp and we will accommodate to the best of our ability.`,
  },
];
